import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Film, Sparkles, Palette } from "lucide-react";

const BrandPlan = () => {
    const [status, setStatus] = useState(null);
    const [pricing, setPricing] = useState(null);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState("");
    const [search] = useSearchParams();

    const refresh = () => api.get("/subscriptions/status").then((r) => setStatus(r.data));
    useEffect(() => {
        refresh();
        api.get("/public/pricing").then((r) => setPricing(r.data));
        api.get("/brand/services").then((r) => setServices(r.data)).catch(() => {});
        if (search.get("demo_paid")) toast.success("Demo purchase applied instantly — no card needed.");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const go = async (key, fn) => {
        setLoading(key);
        try {
            const res = await fn(window.location.origin);
            if (res.data.demo) { toast.success("Applied instantly (demo)"); refresh(); }
            else window.location.href = res.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start checkout");
        } finally { setLoading(""); }
    };

    const subscribe = (planId) => go(`plan-${planId}`, (origin) =>
        api.post("/subscriptions/checkout", { plan_id: planId, origin_url: origin }));
    const topup = (bundleId) => go(`topup-${bundleId}`, (origin) =>
        api.post("/payments/topup/checkout", { bundle_id: bundleId, origin_url: origin }));
    const taster = () => go("taster", (origin) =>
        api.post("/payments/taster/checkout", { origin_url: origin }));
    const portal = () => go("portal", (origin) =>
        api.post("/subscriptions/portal", { origin_url: origin }));

    const videos = status?.videos || {};
    const hasPlan = status?.plan_status === "active";
    const isPastDue = status?.plan_status === "past_due";

    return (
        <DashboardShell role="brand" title="Plan & Videos" subtitle="Your campaign plan, video allowance and top-ups">
            <div className="max-w-5xl mx-auto w-full space-y-6">
                {/* Balance */}
                <div className="yc-card p-8" data-testid="video-balance-card">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <div className="yc-overline">Video allowance</div>
                            <div className="flex items-end gap-3 mt-1">
                                <div className="font-heading text-4xl sm:text-6xl font-black tracking-tighter" data-testid="videos-total">{videos.total ?? 0}</div>
                                <div className="text-yc-ink/60 mb-3">videos available</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                            {[
                                { l: "Plan", v: videos.plan, tid: "videos-plan" },
                                { l: "Rollover", v: videos.rollover, tid: "videos-rollover" },
                                { l: "Top-ups", v: videos.topup, tid: "videos-topup" },
                                { l: "Taster", v: videos.taster, tid: "videos-taster" },
                            ].map((b) => (
                                <div key={b.l} className="border-2 border-yc-ink rounded-2xl px-4 py-3 bg-yc-cream">
                                    <div className="yc-overline text-[10px]">{b.l}</div>
                                    <div className="font-heading font-black text-2xl" data-testid={b.tid}>{b.v ?? 0}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <span data-testid="plan-status-badge" className={`yc-badge ${hasPlan ? "bg-yc-blue text-white" : isPastDue ? "bg-yc-pink text-white" : "bg-yc-cream"}`}>
                            {hasPlan ? `${status?.plan_name} plan · active` : isPastDue ? "Payment past due" : "Free member"}
                        </span>
                        {status?.plan_renews_at && hasPlan && (
                            <span className="text-xs text-yc-ink/60">Renews {new Date(status.plan_renews_at).toLocaleDateString()}</span>
                        )}
                        {status?.taster_valid && <span className="yc-badge bg-yc-pink text-white">Taster unlock active</span>}
                        {(hasPlan || isPastDue) && status?.has_stripe_subscription && (
                            <button data-testid="manage-subscription-btn" disabled={loading === "portal"} onClick={portal} className="yc-btn-secondary text-sm py-2 px-4">
                                {loading === "portal" ? "Opening…" : "Manage / cancel subscription"}
                            </button>
                        )}
                    </div>
                </div>

                {/* Plans */}
                {!hasPlan && (
                    <div className="yc-card p-8">
                        <h3 className="font-heading text-2xl font-bold">Choose a campaign plan</h3>
                        <p className="text-sm text-yc-ink/70 mt-1">One monthly fee, fully managed — the more you commit, the cheaper each video.</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                            {(pricing?.plans || []).map((p) => (
                                <div key={p.id} className={`border-2 border-yc-ink rounded-2xl p-6 flex flex-col ${p.popular ? "bg-yc-blue text-white" : "bg-white"}`} data-testid={`plan-option-${p.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className={`yc-overline ${p.popular ? "!text-white" : ""}`}>{p.name}</div>
                                        {p.popular && <span className="yc-badge bg-yc-pink text-white text-[10px]">Popular</span>}
                                    </div>
                                    <div className="font-heading font-black text-4xl tracking-tighter mt-2">£{p.price.toLocaleString()}<span className="text-base font-bold opacity-70">/mo</span></div>
                                    <div className="text-sm mt-1">{p.videos} videos / month · £{p.per_video}/video</div>
                                    <ul className={`mt-4 space-y-2 text-xs flex-1 ${p.popular ? "text-white/90" : "text-yc-ink/80"}`}>
                                        {p.perks.map((x) => <li key={x} className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {x}</li>)}
                                    </ul>
                                    <button data-testid={`subscribe-${p.id}`} disabled={!!loading} onClick={() => subscribe(p.id)}
                                        className={`w-full mt-5 text-sm py-2 ${p.popular ? "yc-btn-primary bg-white text-yc-ink" : "yc-btn-primary"}`}>
                                        {loading === `plan-${p.id}` ? "Redirecting…" : "Start this plan"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Top-ups */}
                <div className="yc-card p-8" data-testid="topups-card">
                    <h3 className="font-heading text-2xl font-bold flex items-center gap-2"><Film className="w-6 h-6 text-yc-blue" /> Top-up bundles</h3>
                    <p className="text-sm text-yc-ink/70 mt-1">Extra standard videos on any plan — they never expire.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                        {(pricing?.topups || []).map((t) => (
                            <div key={t.id} className="border-2 border-yc-ink rounded-2xl p-5 bg-white flex flex-col" data-testid={`topup-option-${t.id}`}>
                                <div className="yc-overline">{t.name}</div>
                                <div className="font-heading font-black text-3xl tracking-tighter mt-1">£{t.price.toLocaleString()}</div>
                                <div className="text-sm text-yc-ink/70 mt-1 flex-1">{t.videos} extra standard videos</div>
                                <button data-testid={`topup-${t.id}`} disabled={!!loading} onClick={() => topup(t.id)} className="yc-btn-secondary w-full mt-4 text-sm py-2">
                                    {loading === `topup-${t.id}` ? "Redirecting…" : "Buy bundle"}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Creative services — brand-only */}
                {services.length > 0 && (
                    <div className="yc-card p-8" data-testid="creative-services-card">
                        <h3 className="font-heading text-2xl font-bold flex items-center gap-2"><Palette className="w-6 h-6 text-yc-pink" /> Creative services</h3>
                        <p className="text-sm text-yc-ink/70 mt-1">Beyond video — add these to any campaign. Speak to your account manager to book.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                            {services.map((s) => (
                                <div key={s.id} className="border-2 border-yc-ink rounded-2xl p-5 bg-white flex flex-col" data-testid={`service-${s.id}`}>
                                    <div className="flex items-baseline justify-between gap-3">
                                        <div className="font-heading font-bold text-lg">{s.name}</div>
                                        <div className="font-heading font-black text-yc-blue whitespace-nowrap">{s.price_display}</div>
                                    </div>
                                    <div className="text-sm text-yc-ink/70 mt-1">{s.desc}</div>
                                </div>
                            ))}
                        </div>
                        <a href="mailto:info@curated-chaos.co.uk?subject=Creative%20services%20enquiry" data-testid="services-enquire-btn" className="yc-btn-secondary text-sm py-2 px-4 mt-5 inline-flex">Enquire about services</a>
                    </div>
                )}

                {/* Taster */}
                {!hasPlan && (
                    <div className="yc-card p-8 bg-yc-pink text-white" data-testid="taster-card">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                            <div>
                                <div className="flex items-center gap-2"><Sparkles className="w-5 h-5" /><span className="yc-overline text-white">Taster · £{pricing?.taster?.price?.toFixed(0) || "125"}</span></div>
                                <h3 className="font-heading text-2xl font-bold mt-2">Try one video before you commit.</h3>
                                <p className="text-white/90 text-sm mt-2 max-w-xl">One standard video plus a 30-minute strategy call. Unlocks full creator profiles for 30 days — no monthly plan, no lock-in.</p>
                            </div>
                            <button data-testid="buy-taster-btn" disabled={!!loading} onClick={taster} className="yc-btn-primary bg-white text-yc-ink shrink-0">
                                {loading === "taster" ? "Redirecting…" : "Book a taster"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </DashboardShell>
    );
};

export default BrandPlan;
